import React, { useCallback } from 'react'
import {
  SoundbiteDefaultFrame,
  SoundbiteNoHeaderFrame
} from 'components/visualization/SoundbiteFrame'
import { PostMetric } from 'schemas/PostMetricSchema'
import StyledAvatar from 'components/visualization/SoundbiteFrame/StyledAvatar'
import HeaderInfo from 'components/visualization/SoundbiteFrame/HeaderInfo'
import LinkedDate from 'components/visualization/SoundbiteFrame/LinkedDate'
import LinkedDomainIcon from 'components/visualization/SoundbiteFrame/DomainIcon/LinkedDomainIcon'
import PostContent from 'components/visualization/SoundbiteFrame/PostContent/PostContent'
import { useMedia } from 'components/visualization/SoundbiteFrame/Media'
import { MediaClickHandler } from 'components/visualization/SoundbiteFrame/Media/MediaGrid'
import ActionIcons from 'components/visualization/SoundbiteFrame/ActionIcons'
import { isEmpty } from 'lodash'
import PhotoUserTagContent from 'components/visualization/SoundbiteFrame/PhotoUserTagContent'
import { useAppDispatch } from 'app/hooks'
import { openLightbox } from 'features/ui/uiSlice'

import EngagementsPanel from './EngagementsPanel'
import { useStreamData } from '../hooks'
import { useWidgetContext } from '../widgetContext'

interface PostProps {
  data: PostMetric
  /**
   * for test id
   */
  serialNumber: number
  onClick?: (content: any) => void
}
const Post = ({ data, serialNumber, onClick }: PostProps) => {
  const streamData = useStreamData(data)
  const { widgetSetting } = useWidgetContext()
  const dispatch = useAppDispatch()

  const mediaClickHandler: MediaClickHandler = useCallback(
    (mediaUrls, index) => {
      dispatch(openLightbox({ slides: mediaUrls, defaultIndex: index }))
    },
    [dispatch]
  )
  const { mediaController, mediaGrid } = useMedia(streamData, {
    defaultDisplayOption: widgetSetting.defaultMediaDisplayOption,
    onMediaClick: mediaClickHandler
  })

  const {
    isRetweet,
    isTweet,
    replyToAuthors,
    title,
    authorName,
    authorHandle,
    authorUrl,
    authorVerified,
    isMediaDoc,
    photoUserTags
  } = streamData

  if (!title && !authorName && !authorHandle) {
    return (
      <SoundbiteNoHeaderFrame
        avatar={<StyledAvatar streamData={streamData} />}
        dateItem={<LinkedDate streamData={streamData} />}
        domainIcon={<LinkedDomainIcon streamData={streamData} />}
        postContent={<PostContent streamData={streamData} />}
        actionItems={
          <>
            {isMediaDoc && mediaController}
            <ActionIcons streamData={streamData} />
          </>
        }
        mediaItems={isMediaDoc && mediaGrid}
        hasWarningInfo={false}
        photoUserTagContent={
          !isEmpty(photoUserTags) && <PhotoUserTagContent streamData={streamData} />
        }
        metadataItems={<EngagementsPanel data={data} />}
        serialNumber={serialNumber}
        onClick={() => onClick && onClick(streamData)}
      />
    )
  }

  return (
    <SoundbiteDefaultFrame
      serialNumber={serialNumber}
      avatar={<StyledAvatar streamData={streamData} />}
      headerInfo={<HeaderInfo streamData={streamData} />}
      dateItem={<LinkedDate streamData={streamData} />}
      domainIcon={<LinkedDomainIcon streamData={streamData} />}
      replySection={{
        display: !isEmpty(replyToAuthors),
        author: replyToAuthors
      }}
      retweetSection={{
        display: isRetweet! && isTweet!,
        author: authorName,
        url: authorUrl,
        isVerified: authorVerified
      }}
      reblogSection={{
        display: isRetweet! && !isTweet!,
        author: authorName,
        url: authorUrl
      }}
      postContent={<PostContent streamData={streamData} />}
      actionItems={
        <>
          {streamData.isMediaDoc && mediaController}
          <ActionIcons streamData={streamData} />
        </>
      }
      mediaItems={mediaGrid}
      hasWarningInfo={false}
      metadataItems={<EngagementsPanel data={data} />}
      onClick={() => onClick && onClick(streamData)}
    />
  )
}
export default Post
